import PropTypes from "prop-types";
import { useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Form, DatePicker, Input, Select, Checkbox, Button, Divider, TimePicker, InputNumber, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../../app/stores/store";
import FormSection from "./FormSection";
import RichTextEditor from "../../RichTextEditor/RichTextEditor";
import { useDepartment } from "../../../app/hooks/useDepartment";
import { PaginatedResult } from "../../../app/utils/pagination";
import { useDepartmentPaginatedSelect } from "../../../app/hooks/useDepartmentPaginatedSelect";

const { Option } = Select;
const { RangePicker } = DatePicker;
const pageSize = 10;

const JobPostForm = ({ form, employmentType, setEmploymentType, isFetching }) => {
  const { jobPostStore, notificationStore } = useStore();
  const { showNotification } = notificationStore;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const scheduleType = Form.useWatch("scheduleType", form);
  const description = Form.useWatch("description", form);

  const handleEmploymentClick = (type) => {
    setEmploymentType(type);
    form.setFieldsValue({ employmentType: type });
  };

  const handleSaveClick = async () => {
    try {
      await form.validateFields();
      jobPostStore.setIsSaveJobPostingModalVisible(true);
    } catch (error) {
      console.error("Validation failed:", error);
      if (error.errorFields.length > 0) {
        form.scrollToField(error.errorFields[0].name);
      }
    }
  };

  const validateDescription = (_, value) => {
    if (!value) {
      return Promise.reject(new Error("Please input Job Description"));
    }
    const plainText = value.replace(/<[^>]*>/g, "").trim();
    if (!plainText) {
      return Promise.reject(new Error("Please input Job Description"));
    }
    return Promise.resolve();
  };

  const { getDepartments } = useDepartment({
    pageSize: pageSize,
  });

  const fetchDepartments = useCallback(
    async (pageNumber, pageSize, selectSearch = "") => {
      setIsLoading(true);

      try {
        const result = await getDepartments(pageNumber, pageSize, selectSearch);
        return result;
      } catch (error) {
        const errorMessage = error?.response?.data?.details || error?.response?.data?.title || error?.message || "Department not found.";

        showNotification(errorMessage, "error", {
          title: "Department not found.",
          duration: 5000,
        });
        return new PaginatedResult();
      } finally {
        setIsLoading(false);
      }
    },
    [getDepartments, showNotification],
  );

  const { options: departmentsOptions, onPopupScroll, onSearch } = useDepartmentPaginatedSelect(fetchDepartments, pageSize);

  return (
    <Spin spinning={isFetching} tip="Loading job details...">
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          hasSlots: true,
        }}
      >
        <FormSection title="Job title" description="A job title must describe one position only">
          <Form.Item name="title" className="mb-0" rules={[{ required: true, message: "Please input Job Title" }]}>
            <Input size="large" placeholder="Enter Job title" className="py-2 px-3 rounded-md border-gray-300" />
          </Form.Item>
        </FormSection>

        <Divider style={{ borderColor: "#A5A5A5" }} className="my-6 mx-0 p-0" />

        <FormSection title="Job description" description="Provide a short description about the job. Keep it short and to the point.">
          <Form.Item name="description" className="mb-0 p-0" rules={[{ validator: validateDescription }]}>
            <RichTextEditor value={description} onChange={(value) => form.setFieldsValue({ description: value })} placeholder="Enter job description..." />
          </Form.Item>
        </FormSection>

        <Divider style={{ borderColor: "#A5A5A5" }} className="my-6 mx-0 p-0" />

        <FormSection title="Department" description="You can pick multiple work schedules">
          <div className="flex flex-col gap-4">
            <Form.Item name="departmentId" rules={[{ required: true, message: "Please select a department" }]} className="mb-0">
              <Select
                placeholder="Select/Search department"
                size="large"
                loading={isLoading}
                onClear
                showSearch
                onPopupScroll={onPopupScroll}
                onSearch={onSearch}
                filterOption={false}
                options={[
                  { value: undefined, label: "Select/Search department", disabled: true },
                  ...(departmentsOptions?.map((department) => ({
                    value: department.id,
                    label: department.name,
                  })) || []),
                ]}
              ></Select>
            </Form.Item>
          </div>
        </FormSection>

        <Divider style={{ borderColor: "#A5A5A5" }} className="my-6 mx-0 p-0" />

        <FormSection title="Employment type" description="Select type of employment">
          <Form.Item name="employmentType" hidden>
            <Input />
          </Form.Item>

          <div className="flex flex-col gap-3">
            <div
              className={`job-select-option ${employmentType === "Core" ? "job-select-option--selected" : ""}`}
              onClick={() => handleEmploymentClick("Core")}
            >
              <Checkbox checked={employmentType === "Core"} className="text-gray-700">
                Core
              </Checkbox>
            </div>

            <div
              className={`job-select-option ${employmentType === "ProjectBased" ? "job-select-option--selected" : ""}`}
              onClick={() => handleEmploymentClick("ProjectBased")}
            >
              <Checkbox checked={employmentType === "ProjectBased"} className="text-gray-700">
                Project-Based
              </Checkbox>
            </div>
          </div>
        </FormSection>

        <Divider style={{ borderColor: "#A5A5A5" }} className="my-6 mx-0 p-0" />

        <FormSection title="Working schedule" description="You can pick multiple work schedules">
          <div className="flex flex-col gap-4">
            <Form.Item name="scheduleType" rules={[{ required: true, message: "Please select a schedule type" }]} className="mb-0">
              <Select placeholder="Select schedule type" size="large">
                <Option value="Weekdays">Weekdays</Option>
                <Option value="Weekends">Weekends</Option>
                <Option value="Custom">Custom</Option>
              </Select>
            </Form.Item>

            {scheduleType === "Custom" && (
              <Form.Item name="dateRange" rules={[{ required: true, message: "Please select the date range" }]} className="mb-0">
                <RangePicker size="large" className="w-full rounded-lg h-10" placeholder={["Start Date", "End Date"]} />
              </Form.Item>
            )}

            <Form.Item name="shift" className="mb-0" rules={[{ required: true, message: "Please input the shift" }]}>
              <Select placeholder="Day shift" size="large">
                <Option value="day">Day shift</Option>
                <Option value="night">Night shift</Option>
              </Select>
            </Form.Item>

            <div className="flex gap-4">
              <Form.Item name="startTime" className="mb-0 w-1/2" rules={[{ required: true, message: "Please input start time" }]}>
                <TimePicker use12Hours format="h:mm A" size="large" className="w-full" />
              </Form.Item>

              <Form.Item name="endTime" className="mb-0 w-1/2" rules={[{ required: true, message: "Please input end time" }]}>
                <TimePicker use12Hours format="h:mm A" size="large" className="w-full" />
              </Form.Item>
            </div>

            <Form.Item name="location" className="mb-0" rules={[{ required: true, message: "Please input working setup" }]}>
              <Select placeholder="Pick working setup" size="large">
                <Option value="On-Site">On-site</Option>
                <Option value="Hybrid">Hybrid</Option>
              </Select>
            </Form.Item>
          </div>
        </FormSection>

        <Divider style={{ borderColor: "#A5A5A5" }} className="my-6 mx-0 p-0" />

        <FormSection title="Available slots?" description="Applications will close once the maximum number of applicants is reached." noDivider>
          <div className="flex items-center gap-4">
            <Form.Item name="hasSlots" valuePropName="checked" className="mb-0">
              <Checkbox className="scale-125" />
            </Form.Item>

            <Form.Item
              name="availableSlots"
              className="mb-0 w-full"
              rules={[
                { required: true, message: "Please enter the number of slots." },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (getFieldValue("hasSlots") && value && value < 1) {
                      return Promise.reject("Please enter a valid number of slots.");
                    }
                    if (value && value <= 0) {
                      return Promise.reject("Slots must be a positive number.");
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber size="large" className="w-full" min={1} placeholder="Enter number of slots" />
            </Form.Item>
          </div>
        </FormSection>

        <div className="flex justify-end gap-6 mt-12">
          <Button
            size="large"
            className="w-40 !border-orange-500 !text-orange-500 !hover:text-orange-700 !hover:border-orange-700 font-semibold rounded-lg"
            onClick={() => navigate(`/recruitment/job-openings`)}
          >
            CANCEL
          </Button>

          <Button
            type="primary"
            size="large"
            className="w-40 border-none font-semibold rounded-lg hover:opacity-90"
            style={{ backgroundColor: "#F45F0C", letterSpacing: "1px" }}
            onClick={handleSaveClick}
          >
            SAVE
          </Button>
        </div>
      </Form>
    </Spin>
  );
};

JobPostForm.propTypes = {
  form: PropTypes.object.isRequired,
  employmentType: PropTypes.string,
  setEmploymentType: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
};

export default observer(JobPostForm);
